import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";

const COLLECTION = "tblQuadroCompetencias";

const COMPETENCIAS_PADRAO = [
  {
    cargo: "Diretor Técnico",
    responsabilidades: "Responsável legal e técnico (5.1.1, 5.3)\nAprovar documentos técnicos (8.5.1)\nGarantir conformidade com normas e requisitos estatutários (8.2.2, 8.6)",
    requisitos: "Formação em Engenharia Civil\nRegistro ativo no CREA\nConhecimento da NBR 5739",
    treinamentos: "Interpretação da ISO 9001:2015\nResponsabilidade técnica\nRequisitos legais e técnicos",
  },
  {
    cargo: "Coordenador da Qualidade",
    responsabilidades: "Planejar, implementar e manter o SGQ (4.4, 6.2, 9.1)\nGerenciar auditorias (9.2)\nControlar documentos (7.5)\nCoordenar análise crítica da direção (9.3)",
    requisitos: "Conhecimento da ISO 9001:2015\nGestão da Qualidade\nHabilidade analítica",
    treinamentos: "Formação em ISO 9001:2015\nAuditoria Interna da Qualidade\nGestão de riscos e documentos",
  },
  {
    cargo: "Encarregado de Laboratório",
    responsabilidades: "Controlar operações de ensaio (8.5)\nAssegurar conformidade de produto (8.6)\nParticipar na liberação de produtos (8.6)\nConduzir ações corretivas (10.2)",
    requisitos: "Interpretação de normas técnicas\nPlanejamento de ensaios\nLiderança técnica",
    treinamentos: "Execução e controle de ensaios conforme NBR\nAções corretivas e preventivas\nRequisitos de produto\nIntegração ao SGQ\nPráticas laboratoriais supervisionadas",
  },
  {
    cargo: "Auxiliar de Laboratório",
    responsabilidades: "Executar atividades de produção e controle (8.5.1)\nIdentificar e rastrear CPs (8.5.2)\nCumprir instruções de trabalho documentadas (7.5)",
    requisitos: "Ensino médio técnico\nTreinamento interno\nOrganização e atenção a detalhes",
    treinamentos: "Boas práticas laboratoriais\nProcedimentos de moldagem e ensaio\nRastreabilidade e registros\nPrograma de Treinamento",
  },
  {
    cargo: "Responsável por Equipamentos",
    responsabilidades: "Assegurar calibração e verificação dos equipamentos (7.1.5.2)\nGerar e manter registros metrológicos (7.5.3)\nApoiar controle da medição (8.5.1)",
    requisitos: "Conhecimento em metrologia\nGestão de calibração\nRegistro técnico",
    treinamentos: "Calibração e verificação de equipamentos\nGestão de registros metrológicos\nBoas práticas de manutenção",
  },
  {
    cargo: "Atendimento/Administração",
    responsabilidades: "Atender clientes (8.2.1)\nGerenciar entradas de serviço (8.2.2)\nControlar registros e entregas (7.5.3, 8.6)\nApoiar satisfação do cliente (9.1.2)",
    requisitos: "Boa comunicação\nOrganização administrativa\nConhecimento em informática",
    treinamentos: "Atendimento ao cliente\nControle de pedidos e rastreabilidade\nProcedimentos administrativos do SGQ",
  },
  {
    cargo: "Estagiário",
    responsabilidades: "Apoiar nas atividades sob supervisão (7.1.2)\nContribuir para execução dos processos (8.5.1)\nParticipar de treinamentos e desenvolvimento (7.2)",
    requisitos: "Estar cursando Engenharia Civil\nInteresse e iniciativa\nResponsabilidade",
    treinamentos: "Integração ao SGQ\nPráticas laboratoriais supervisionadas\nIntrodução às normas e procedimentos internos",
  },
  {
    cargo: "Técnico em Laboratório",
    responsabilidades: "Controlar operações de ensaio (8.5)\nAssegurar conformidade de produto (8.6)\nParticipar na liberação de produtos (8.6)\nConduzir ações corretivas (10.2)",
    requisitos: "Interpretação de normas técnicas\nPlanejamento de ensaios\nLiderança técnica",
    treinamentos: "Execução e controle de ensaios conforme NBR\nAções corretivas e preventivas\nRequisitos de produto\nIntegração ao SGQ\nPráticas laboratoriais supervisionadas",
  },
];

function criarQuadroInicial() {
  return {
    codigo: "POP 5.1",
    versao: "01",
    aprovadoEm: "2025-08-20",
    competencias: COMPETENCIAS_PADRAO.map((item) => ({ ...item })),
  };
}

function formatarData(data) {
  if (!data) return "-";
  const [ano, mes, dia] = String(data).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : data;
}

function QuadroVisual({ item, onEditar, onAdicionarCargo }) {
  const competencias = Array.isArray(item.competencias) ? item.competencias : [];

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="body2" fontWeight={700}>
          {item.codigo || "POP 5.1"} — Versão {item.versao || "-"} — Aprovado em {formatarData(item.aprovadoEm)}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onEditar(item)}>Editar quadro</Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => onAdicionarCargo(item)}>Novo cargo</Button>
          <HistoricoCadastro colecao={COLLECTION} documentoId={item.id} registro={item} />
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1050, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ width: "16%", backgroundColor: "#063b70", color: "white", fontWeight: 800, border: "1px solid #94a3b8" }}>Cargo</TableCell>
              <TableCell align="center" sx={{ width: "31%", backgroundColor: "#063b70", color: "white", fontWeight: 800, border: "1px solid #94a3b8" }}>Principais Responsabilidades</TableCell>
              <TableCell align="center" sx={{ width: "25%", backgroundColor: "#063b70", color: "white", fontWeight: 800, border: "1px solid #94a3b8" }}>Requisitos Técnicos / Competências</TableCell>
              <TableCell align="center" sx={{ width: "28%", backgroundColor: "#063b70", color: "white", fontWeight: 800, border: "1px solid #94a3b8" }}>Treinamentos</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {competencias.map((competencia, indice) => (
              <TableRow key={`${competencia.cargo}-${indice}`}>
                <TableCell sx={{ border: "1px solid #94a3b8", fontWeight: 700, whiteSpace: "pre-line" }}>{competencia.cargo || "-"}</TableCell>
                <TableCell sx={{ border: "1px solid #94a3b8", whiteSpace: "pre-line" }}>{competencia.responsabilidades || "-"}</TableCell>
                <TableCell sx={{ border: "1px solid #94a3b8", whiteSpace: "pre-line" }}>{competencia.requisitos || "-"}</TableCell>
                <TableCell sx={{ border: "1px solid #94a3b8", whiteSpace: "pre-line" }}>{competencia.treinamentos || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

export default function CadastroQuadroCompetencias() {
  const [quadros, setQuadros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [quadroEditando, setQuadroEditando] = useState(null);
  const [quadro, setQuadro] = useState(criarQuadroInicial());
  const [notificacao, setNotificacao] = useState({ aberta: false, mensagem: "", tipo: "success" });
  const cadastrandoPadraoRef = useRef(false);

  const quadrosExibidos = useMemo(() => {
    const chaves = new Set();
    return quadros.filter((item) => {
      const chave = `${item.codigo || ""}|${item.versao || ""}|${item.aprovadoEm || ""}`;
      if (chaves.has(chave)) return false;
      chaves.add(chave);
      return true;
    });
  }, [quadros]);

  async function carregarQuadros() {
    setCarregando(true);
    try {
      const snapshot = await getDocs(consultaTenantAtual(query(collection(db, COLLECTION), orderBy("criadoEm", "desc"))));
      setQuadros(snapshot.docs.map((documento) => ({ id: documento.id, ...documento.data() })));
    } catch (error) {
      console.error("Erro ao carregar Quadro de Competências:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível carregar o Quadro de Competências.", tipo: "error" });
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarQuadros();
  }, []);

  function dadosParaEdicao(item, adicionarCargo = false) {
    return {
      codigo: item.codigo || "POP 5.1",
      versao: item.versao || "01",
      aprovadoEm: item.aprovadoEm || "",
      competencias: [
        ...(Array.isArray(item.competencias) ? item.competencias.map((competencia) => ({ ...competencia })) : []),
        ...(adicionarCargo ? [{ cargo: "", responsabilidades: "", requisitos: "", treinamentos: "" }] : []),
      ],
    };
  }

  function abrirEdicao(item) {
    setQuadroEditando(item);
    setQuadro(dadosParaEdicao(item));
    setDialogAberto(true);
  }

  function abrirNovoCargo(item) {
    setQuadroEditando(item);
    setQuadro(dadosParaEdicao(item, true));
    setDialogAberto(true);
  }

  async function cadastrarQuadroPadrao() {
    if (quadros.length > 0 || cadastrandoPadraoRef.current) return;
    cadastrandoPadraoRef.current = true;
    setSalvando(true);
    const quadroInicial = criarQuadroInicial();
    try {
      const documentoCriado = await addDoc(collection(db, COLLECTION), aplicarTenantAtual({
        ...quadroInicial,
        criadoEm: serverTimestamp(),
        criadoPor: auth.currentUser?.uid || null,
        atualizadoEm: serverTimestamp(),
        atualizadoPor: auth.currentUser?.uid || null,
      }));
      setQuadros([{ id: documentoCriado.id, ...quadroInicial }]);
      setNotificacao({ aberta: true, mensagem: "Quadro de Competências padrão cadastrado com sucesso.", tipo: "success" });
    } catch (error) {
      console.error("Erro ao cadastrar Quadro de Competências padrão:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível cadastrar o quadro padrão.", tipo: "error" });
    } finally {
      cadastrandoPadraoRef.current = false;
      setSalvando(false);
    }
  }

  function atualizarCampo(campo, valor) {
    setQuadro((atual) => ({ ...atual, [campo]: valor }));
  }

  function atualizarCompetencia(indice, campo, valor) {
    setQuadro((atual) => ({
      ...atual,
      competencias: atual.competencias.map((item, index) => index === indice ? { ...item, [campo]: valor } : item),
    }));
  }

  function adicionarCargo() {
    setQuadro((atual) => ({ ...atual, competencias: [...atual.competencias, { cargo: "", responsabilidades: "", requisitos: "", treinamentos: "" }] }));
  }

  function removerCargo(indice) {
    setQuadro((atual) => ({ ...atual, competencias: atual.competencias.filter((_, index) => index !== indice) }));
  }

  async function salvar() {
    if (!quadro.codigo.trim() || !quadro.versao.trim()) {
      setNotificacao({ aberta: true, mensagem: "Informe o código e a versão.", tipo: "warning" });
      return;
    }
    if (quadro.competencias.some((item) => !item.cargo.trim())) {
      setNotificacao({ aberta: true, mensagem: "Informe o cargo em todas as linhas.", tipo: "warning" });
      return;
    }

    setSalvando(true);
    try {
      await updateDoc(doc(db, COLLECTION, quadroEditando.id), {
        codigo: quadro.codigo.trim(),
        versao: quadro.versao.trim(),
        aprovadoEm: quadro.aprovadoEm,
        competencias: quadro.competencias.map((item) => ({
          cargo: item.cargo.trim(),
          responsabilidades: item.responsabilidades.trim(),
          requisitos: item.requisitos.trim(),
          treinamentos: item.treinamentos.trim(),
        })),
        atualizadoEm: serverTimestamp(),
        atualizadoPor: auth.currentUser?.uid || null,
      });
      setDialogAberto(false);
      setNotificacao({ aberta: true, mensagem: "Quadro de Competências atualizado com sucesso.", tipo: "success" });
      await carregarQuadros();
    } catch (error) {
      console.error("Erro ao salvar Quadro de Competências:", error);
      setNotificacao({ aberta: true, mensagem: "Não foi possível salvar o Quadro de Competências.", tipo: "error" });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Navbar />
      <Box sx={{ p: { xs: 2, md: 4 }, mt: 6 }}>
        <Paper className="cabecalho-lista-cadastro" elevation={0} sx={{ mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ md: "center" }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Quadro de Competências</Typography>
              <Typography variant="body2" color="text.secondary">POP 5.1 — Sistema de Gestão da Qualidade</Typography>
            </Box>
            {quadros.length === 0 && !carregando && <Button variant="outlined" onClick={cadastrarQuadroPadrao} disabled={salvando}>Cadastrar quadro padrão</Button>}
          </Stack>

          {carregando && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={24} /></Box>}
          {!carregando && quadros.length === 0 && <Box sx={{ p: 4, textAlign: "center" }}>Nenhum Quadro de Competências cadastrado.</Box>}
          {!carregando && quadrosExibidos.map((item) => <QuadroVisual key={item.id} item={item} onEditar={abrirEdicao} onAdicionarCargo={abrirNovoCargo} />)}
        </Paper>
      </Box>

      <Dialog open={dialogAberto} onClose={() => !salvando && setDialogAberto(false)} fullWidth maxWidth="xl">
        <DialogTitle>Editar Quadro de Competências</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField label="Código" value={quadro.codigo} onChange={(event) => atualizarCampo("codigo", event.target.value)} fullWidth required />
              <TextField label="Versão" value={quadro.versao} onChange={(event) => atualizarCampo("versao", event.target.value)} fullWidth required />
              <TextField label="Aprovado em" type="date" value={quadro.aprovadoEm} onChange={(event) => atualizarCampo("aprovadoEm", event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>

            <Paper variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead><TableRow><TableCell>Cargo</TableCell><TableCell>Principais Responsabilidades</TableCell><TableCell>Requisitos Técnicos / Competências</TableCell><TableCell>Treinamentos</TableCell><TableCell /></TableRow></TableHead>
                <TableBody>{quadro.competencias.map((item, indice) => <TableRow key={indice}>
                  <TableCell sx={{ minWidth: 190 }}><TextField value={item.cargo} onChange={(event) => atualizarCompetencia(indice, "cargo", event.target.value)} size="small" multiline minRows={2} fullWidth required /></TableCell>
                  <TableCell sx={{ minWidth: 300 }}><TextField value={item.responsabilidades} onChange={(event) => atualizarCompetencia(indice, "responsabilidades", event.target.value)} size="small" multiline minRows={5} fullWidth /></TableCell>
                  <TableCell sx={{ minWidth: 260 }}><TextField value={item.requisitos} onChange={(event) => atualizarCompetencia(indice, "requisitos", event.target.value)} size="small" multiline minRows={5} fullWidth /></TableCell>
                  <TableCell sx={{ minWidth: 280 }}><TextField value={item.treinamentos} onChange={(event) => atualizarCompetencia(indice, "treinamentos", event.target.value)} size="small" multiline minRows={5} fullWidth /></TableCell>
                  <TableCell><Tooltip title="Remover cargo"><IconButton size="small" color="error" onClick={() => removerCargo(indice)}><DeleteOutlineIcon /></IconButton></Tooltip></TableCell>
                </TableRow>)}</TableBody>
              </Table>
            </Paper>
            <Button startIcon={<AddIcon />} onClick={adicionarCargo} sx={{ alignSelf: "flex-start" }}>Adicionar cargo</Button>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogAberto(false)} disabled={salvando}>Cancelar</Button><Button variant="contained" onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</Button></DialogActions>
      </Dialog>

      <Snackbar open={notificacao.aberta} autoHideDuration={5000} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}><Alert severity={notificacao.tipo} onClose={() => setNotificacao((atual) => ({ ...atual, aberta: false }))}>{notificacao.mensagem}</Alert></Snackbar>
    </>
  );
}
